/**
 * SolarLunarDockPanel.ts
 *
 * Dedicated full-width bottom calendar dock for Long-Term Solar & Lunar Ephemeris
 * and Dark Skies Stargazing Outlook.
 *
 * Features:
 * - 12-Month Calendar with daily max darkness ratings and pitch-black dark hours.
 * - Mini 24h diurnal sparklines in every calendar cell.
 * - Full-Month continuous 744-hour diurnal darkness wave with interactive hover & scrub.
 * - Real-time on-the-fly dynamic map canvas overlay evaluating solar & lunar positions
 *   over the North American Bortle light pollution grid.
 * - Map layer toggles for underlying Bortle basemap and Darkness overlay.
 * - Dedicated and master-synchronized opacity controls (defaulting to 80%).
 * - High-precision Sun & Moon calculations (equatorial/horizontal coordinates, phases,
 *   illumination, and astronomical twilight factoring).
 * - Clear methodology disclaimer:
 *   "Outlook based on static Bortle and solar/lunar phases, no atmospheric data. Intended for long term outlook/planning."
 */

import store, { AppState } from '../../state';
import { getBortleAt } from '../../services/PoiDataService';
import bortleGridData from '../../config/bortle_grid_na.json';
import * as SunCalc from 'suncalc';
import { getApiServer, buildPmtilesUrl } from '../../config';

interface AstroCelestial {
  gmst: number;
  sun: { ra: number; dec: number; lam: number; eps: number };
  moon: { ra: number; dec: number; fraction: number; phase: number };
}

interface DailyDarknessMetrics {
  date: Date;
  dateStr: string;
  maxScore: number;
  darkHours: number;
  darkStartMs: number | null;
  darkEndMs: number | null;
  hourlyScores: number[];
  phaseInfo: { name: string; icon: string; fraction: number; percent: string };
  sTimes: any;
  mTimes: any;
}

export class SolarLunarDockPanel {
  private static instance: SolarLunarDockPanel | null = null;
  private container: HTMLElement | null = null;
  private isCollapsed: boolean = false;
  private unsubscribe: (() => void) | null = null;
  private map: any = null;

  // Selected State
  private currentYear: number;
  private currentMonth: number; // 0 - 11
  private baseYear: number;
  private baseMonth: number;
  private selectedDate: Date;
  private selectedHour: number = 22; // default to 22:00 local
  private currentBortle: number = 3;
  private bortleOverride: string = 'auto'; // 'auto' or '1'..'9'
  private lastPoiKey: string = '';

  // Map Layer State
  private mapCanvas: HTMLCanvasElement | null = null;
  private mapCtx: CanvasRenderingContext2D | null = null;
  private bortleArray: Uint8Array | null = null;
  private gridBounds = { west: -130.0, east: -60.0, south: 20.0, north: 60.0 };
  private gridRows = 88;
  private gridCols = 110;

  private overlayEnabled: boolean = true;
  private bortleBaseEnabled: boolean = true;
  private mapOpacity: number = 0.80; // 80% default matching production dark skies

  // Playback State
  private isPlaying: boolean = false;
  private playIntervalId: any = null;
  private playbackSpeed: number = 1.0;
  private isLooping: boolean = true;

  // Cached Metrics (dateStr -> DailyDarknessMetrics)
  private metricsCache: Map<string, DailyDarknessMetrics> = new Map();

  // Astro Math constants
  private readonly rad = Math.PI / 180;
  private readonly deg = 180 / Math.PI;

  private constructor() {
    const now = new Date();
    this.currentYear = now.getFullYear();
    this.currentMonth = now.getMonth();
    this.baseYear = now.getFullYear();
    this.baseMonth = now.getMonth();
    this.selectedDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    this.initBortleGrid();
  }

  public static getInstance(): SolarLunarDockPanel {
    if (!SolarLunarDockPanel.instance) {
      SolarLunarDockPanel.instance = new SolarLunarDockPanel();
    }
    return SolarLunarDockPanel.instance;
  }

  public setMap(mapInstance: any): void {
    this.map = mapInstance;
  }

  private initBortleGrid(): void {
    try {
      const data = bortleGridData as any;
      if (data && data.grid50km) {
        this.gridRows = data.grid50km.rows;
        this.gridCols = data.grid50km.cols;
        this.gridBounds = data.bounds;
        const binStr = atob(data.grid50km.bortleB64);
        const len = binStr.length;
        this.bortleArray = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
          this.bortleArray[i] = binStr.charCodeAt(i);
        }
      }
    } catch (e) {
      console.warn('[SolarLunarDockPanel] Could not unpack Bortle grid data:', e);
    }
  }

  public open(): void {
    if (!this.container) {
      this.initDom();
    }
    if (this.container) {
      this.container.style.display = 'flex';
    }

    // Hide master timeline to prevent layout collision
    const timelineEl = document.getElementById('timeline-container');
    if (timelineEl) timelineEl.style.display = 'none';

    // Collapse docked trend chart to maximize dock visibility
    store.update({ dockedTrendCollapsed: true });

    if (!this.unsubscribe) {
      this.unsubscribe = store.subscribe((state) => {
        if (this.container && this.container.style.display !== 'none') {
          this.render(state);
        }
      });
    }

    this.ensureMapLayers();
    this.render(store.getState());
    this.updateMapLayer();
  }

  public close(): void {
    this.pausePlay();
    if (this.container) {
      this.container.style.display = 'none';
    }

    // Restore master timeline
    const timelineEl = document.getElementById('timeline-container');
    if (timelineEl) timelineEl.style.display = '';

    // Hide map layers
    if (this.map) {
      if (this.map.getLayer('ephem-darkskies-layer')) {
        this.map.setLayoutProperty('ephem-darkskies-layer', 'visibility', 'none');
      }
      if (this.map.getLayer('ephem-bortle-base-layer')) {
        this.map.setLayoutProperty('ephem-bortle-base-layer', 'visibility', 'none');
      }
    }

    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = null;
    }
  }

  public isOpen(): boolean {
    return !!this.container && this.container.style.display !== 'none';
  }

  private initDom(): void {
    this.container = document.createElement('div');
    this.container.id = 'solar-lunar-bottom-dock';
    this.container.className = 'solar-lunar-bottom-dock';
    this.container.style.display = 'none';
    document.body.appendChild(this.container);

    window.addEventListener('resize', () => {
      if (this.isOpen()) {
        this.drawMonthlyDiurnalCurve();
      }
    });
  }

  // --- Astronomical Coordinate & Dark Skies Math ---

  private toDays(date: Date): number {
    return date.getTime() / 86400000 + 2440587.5 - 2451545.0;
  }

  private gmst(d: number): number {
    return ((280.46061837 + 360.98564736629 * d) % 360 + 360) % 360;
  }

  private sunCoords(d: number): { ra: number; dec: number; lam: number; eps: number } {
    const M = ((357.5291 + 0.98560028 * d) % 360 + 360) % 360 * this.rad;
    const L = ((280.4665 + 0.98564736 * d) % 360 + 360) % 360 * this.rad;
    const lam = L + (1.9148 * Math.sin(M) + 0.02 * Math.sin(2 * M)) * this.rad;
    const eps = (23.4393 - 0.0000004 * d) * this.rad;
    const sinDec = Math.sin(eps) * Math.sin(lam);
    const dec = Math.asin(Math.max(-1, Math.min(1, sinDec)));
    const ra = (Math.atan2(Math.cos(eps) * Math.sin(lam), Math.cos(lam)) * this.deg + 360) % 360;
    return { ra, dec, lam, eps };
  }

  private moonCoords(d: number, sun: { lam: number; eps: number }): { ra: number; dec: number; fraction: number; phase: number } {
    const Lm = ((218.3165 + 13.17639648 * d) % 360 + 360) % 360 * this.rad;
    const Mm = ((134.9634 + 13.06499295 * d) % 360 + 360) % 360 * this.rad;
    const F = ((93.2721 + 13.22935024 * d) % 360 + 360) % 360 * this.rad;
    const D = ((350.7377 + 12.19074919 * d) % 360 + 360) % 360 * this.rad;

    const lamM = Lm + (6.289 * Math.sin(Mm) - 1.274 * Math.sin(Mm - 2 * D) + 0.658 * Math.sin(2 * D) - 0.214 * Math.sin(2 * Mm)) * this.rad;
    const betaM = (5.128 * Math.sin(F)) * this.rad;

    const eps = sun.eps;
    const sinDec = Math.sin(betaM) * Math.cos(eps) + Math.cos(betaM) * Math.sin(eps) * Math.sin(lamM);
    const dec = Math.asin(Math.max(-1, Math.min(1, sinDec)));
    const y = Math.cos(betaM) * Math.cos(eps) * Math.sin(lamM) - Math.sin(betaM) * Math.sin(eps);
    const x = Math.cos(betaM) * Math.cos(lamM);
    const ra = (Math.atan2(y, x) * this.deg + 360) % 360;

    const elong = Math.acos(Math.max(-1, Math.min(1, Math.cos(betaM) * Math.cos(lamM - sun.lam))));
    const fraction = (1 - Math.cos(elong)) / 2;
    const phase = (((lamM - sun.lam) * this.deg % 360 + 360) % 360) / 360;

    return { ra, dec, fraction, phase };
  }

  private altitude(latDeg: number, lonDeg: number, gmstDeg: number, raDeg: number, decRad: number): number {
    const latRad = latDeg * this.rad;
    const lha = ((gmstDeg + lonDeg - raDeg) % 360 + 360) % 360 * this.rad;
    const sinAlt = Math.sin(latRad) * Math.sin(decRad) + Math.cos(latRad) * Math.cos(decRad) * Math.cos(lha);
    return Math.asin(Math.max(-1, Math.min(1, sinAlt))) * this.deg;
  }

  private moonAltitude(latDeg: number, lonDeg: number, gmstDeg: number, raDeg: number, decRad: number): number {
    const altGeo = this.altitude(latDeg, lonDeg, gmstDeg, raDeg, decRad);
    const parallax = 0.95 * Math.cos(Math.max(0, altGeo) * this.rad);
    return altGeo - parallax;
  }

  private getCelestial(date: Date): AstroCelestial {
    const d = this.toDays(date);
    const gmstVal = this.gmst(d);
    const sun = this.sunCoords(d);
    const moon = this.moonCoords(d, sun);
    return { gmst: gmstVal, sun, moon };
  }

  private getMoonPhaseInfo(phase: number, fraction: number): { name: string; icon: string; fraction: number; percent: string } {
    const b = Math.round(phase * 8) % 8;
    const names = ["New Moon", "Waxing Crescent", "First Quarter", "Waxing Gibbous", "Full Moon", "Waning Gibbous", "Last Quarter", "Waning Crescent"];
    const icons = ["🌑", "🌒", "🌓", "🌔", "🌕", "🌖", "🌗", "🌘"];
    return {
      name: names[b],
      icon: icons[b],
      fraction,
      percent: Math.round(fraction * 100) + '%'
    };
  }

  private calculateScore(sunAlt: number, moonAlt: number, moonFraction: number, bortle: number): number {
    const baseScore = Math.max(1.0, Math.min(10.0, 10.0 - (bortle - 1.0) * 1.1));
    let moonPenalty = 0;
    if (moonAlt > 0) {
      moonPenalty = moonFraction * Math.sqrt(Math.sin(moonAlt * this.rad)) * 7.0;
    }
    let sunFactor = 0;
    if (sunAlt <= -18.0) {
      sunFactor = 1.0;
    } else if (sunAlt < -6.0) {
      sunFactor = Math.pow((sunAlt - (-6.0)) / (-18.0 - (-6.0)), 4);
    }
    return Math.max(0.0, Math.min(10.0, (baseScore - moonPenalty) * sunFactor));
  }

  private calculateDailyDarkness(date: Date, lat: number, lon: number, bortle: number): DailyDarknessMetrics {
    const y = date.getFullYear();
    const m = date.getMonth();
    const d = date.getDate();

    let maxDarkScore = 0;
    let darkMinutes = 0;
    let darkStartMs: number | null = null;
    let darkEndMs: number | null = null;
    const hourlyScores: number[] = [];

    for (let h = 0; h < 24; h++) {
      const stepDate = new Date(y, m, d, h, 0, 0);
      const astro = this.getCelestial(stepDate);
      const sAlt = this.altitude(lat, lon, astro.gmst, astro.sun.ra, astro.sun.dec);
      const mAlt = this.moonAltitude(lat, lon, astro.gmst, astro.moon.ra, astro.moon.dec);
      const sc = this.calculateScore(sAlt, mAlt, astro.moon.fraction, bortle);
      hourlyScores.push(Math.round(sc * 10) / 10);
    }

    const nightStartMs = new Date(y, m, d, 17, 0, 0).getTime();
    const nightEndMs = nightStartMs + 15 * 3600 * 1000;
    const stepIntervalMs = 15 * 60 * 1000;

    for (let tMs = nightStartMs; tMs <= nightEndMs; tMs += stepIntervalMs) {
      const tDate = new Date(tMs);
      const astro = this.getCelestial(tDate);
      const sAlt = this.altitude(lat, lon, astro.gmst, astro.sun.ra, astro.sun.dec);
      const mAlt = this.moonAltitude(lat, lon, astro.gmst, astro.moon.ra, astro.moon.dec);
      const sc = this.calculateScore(sAlt, mAlt, astro.moon.fraction, bortle);

      if (sc > maxDarkScore) maxDarkScore = sc;

      const isSunDark = sAlt <= -18.0;
      const isMoonDark = mAlt <= 0.0 || astro.moon.fraction <= 0.03;
      if (isSunDark && isMoonDark) {
        darkMinutes += 15;
        if (darkStartMs === null) darkStartMs = tMs;
        darkEndMs = tMs;
      }
    }

    const darkHours = Math.round((darkMinutes / 60) * 10) / 10;
    const refAstro = this.getCelestial(new Date(y, m, d, 22, 0, 0));
    const phaseInfo = this.getMoonPhaseInfo(refAstro.moon.phase, refAstro.moon.fraction);

    let sTimes: any = null;
    let mTimes: any = null;
    try {
      sTimes = SunCalc.getTimes(new Date(y, m, d, 12, 0, 0), lat, lon);
      mTimes = SunCalc.getMoonTimes(new Date(y, m, d, 12, 0, 0), lat, lon);
    } catch (_) {}

    return {
      date,
      dateStr: `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`,
      maxScore: Math.round(maxDarkScore * 10) / 10,
      darkHours,
      darkStartMs,
      darkEndMs,
      hourlyScores,
      phaseInfo,
      sTimes,
      mTimes
    };
  }

  private getEffectiveBortle(): number {
    if (this.bortleOverride !== 'auto') {
      const parsed = parseInt(this.bortleOverride, 10);
      if (!isNaN(parsed) && parsed >= 1 && parsed <= 9) return parsed;
    }
    return this.currentBortle || 3;
  }

  // Exact Production Dark Skies Color Palette (0=Red, 2=Orange, 4=Yellow, 6=Green, 8=Cyan, 10=Deep Blue)
  private getDarkSkiesRgb(score: number): { red: number; green: number; blue: number } {
    let r = 0, g = 0, b = 0;
    if (score <= 2.0) {
      const f = Math.max(0, score / 2.0);
      r = Math.round(180 + (255 - 180) * f);
      g = Math.round(0 + (128 - 0) * f);
      b = 0;
    } else if (score <= 4.0) {
      const f = (score - 2.0) / 2.0;
      r = 255;
      g = Math.round(128 + (255 - 128) * f);
      b = 0;
    } else if (score <= 6.0) {
      const f = (score - 4.0) / 2.0;
      r = Math.round(255 * (1 - f));
      g = 255;
      b = 0;
    } else if (score <= 8.0) {
      const f = (score - 6.0) / 2.0;
      r = 0;
      g = 255;
      b = Math.round(255 * f);
    } else {
      const f = Math.min(1, (score - 8.0) / 2.0);
      r = 0;
      g = Math.round(255 * (1 - f * 0.80)); // 255 -> 50
      b = 255;
    }
    return { red: r, green: g, blue: b };
  }

  private getDarkSkiesHex(score: number): string {
    const { red, green, blue } = this.getDarkSkiesRgb(score);
    return `rgb(${red},${green},${blue})`;
  }

  // --- Map Layer Management ---

  private ensureMapLayers(): void {
    if (!this.map) return;

    // 1. Bortle Baseline Basemap Layer
    const manifest = store.getState().manifest;
    const apiServer = getApiServer(manifest);
    const tileRoot = (manifest ? manifest.tile_base : 'tiles').replace(/^\/+|\/+$/g, '');
    const bortleUrl = buildPmtilesUrl(`${apiServer}/${tileRoot}/basemaps/bortle_baseline_0_4.pmtiles`);

    if (!this.map.getSource('ephem-bortle-base-source')) {
      this.map.addSource('ephem-bortle-base-source', {
        type: 'raster',
        url: bortleUrl,
        tileSize: 256,
        minzoom: 0,
        maxzoom: 4
      });
    }

    const beforeLayer = this.map.getLayer('hiking-trails-casing') ? 'hiking-trails-casing' : undefined;

    if (!this.map.getLayer('ephem-bortle-base-layer')) {
      this.map.addLayer({
        id: 'ephem-bortle-base-layer',
        type: 'raster',
        source: 'ephem-bortle-base-source',
        paint: {
          'raster-opacity': 0.70,
          'raster-resampling': 'linear'
        }
      }, beforeLayer);
    }
  }

  public updateMapLayer(): void {
    if (!this.map || !this.bortleArray || !this.isOpen()) return;

    const effBortle = this.getEffectiveBortle();
    const rows = this.gridRows;
    const cols = this.gridCols;

    if (!this.mapCanvas) {
      this.mapCanvas = document.createElement('canvas');
      this.mapCanvas.width = cols;
      this.mapCanvas.height = rows;
      this.mapCtx = this.mapCanvas.getContext('2d', { willReadFrequently: true });
    }

    if (!this.mapCtx) return;

    const y = this.selectedDate.getFullYear();
    const m = this.selectedDate.getMonth();
    const d = this.selectedDate.getDate();
    const evalDate = new Date(y, m, d, this.selectedHour, 0, 0);

    const astro = this.getCelestial(evalDate);
    const gmst = astro.gmst;
    const sunRa = astro.sun.ra;
    const sunDec = astro.sun.dec;
    const moonRa = astro.moon.ra;
    const moonDec = astro.moon.dec;
    const moonIllum = astro.moon.fraction;

    const bounds = this.gridBounds;
    const imgData = this.mapCtx.createImageData(cols, rows);
    const data32 = new Uint32Array(imgData.data.buffer);

    const sinDecS = Math.sin(sunDec), cosDecS = Math.cos(sunDec);
    const sinDecM = Math.sin(moonDec), cosDecM = Math.cos(moonDec);

    const latsRad = new Float32Array(rows);
    for (let r = 0; r < rows; r++) {
      const latDeg = bounds.north - (r / (rows - 1)) * (bounds.north - bounds.south);
      latsRad[r] = latDeg * this.rad;
    }

    const lonsDeg = new Float32Array(cols);
    for (let c = 0; c < cols; c++) {
      lonsDeg[c] = bounds.west + (c / (cols - 1)) * (bounds.east - bounds.west);
    }

    for (let r = 0; r < rows; r++) {
      const latRad = latsRad[r];
      const sinLat = Math.sin(latRad), cosLat = Math.cos(latRad);
      const rowOffset = r * cols;

      for (let c = 0; c < cols; c++) {
        const lon = lonsDeg[c];

        // Solar altitude
        const lhaS = ((gmst + lon - sunRa) % 360 + 360) % 360 * this.rad;
        const sinAltS = sinLat * sinDecS + cosLat * cosDecS * Math.cos(lhaS);
        const altS = Math.asin(Math.max(-1, Math.min(1, sinAltS))) * this.deg;

        if (altS >= -6.0) {
          data32[rowOffset + c] = 0;
          continue;
        }

        // Lunar altitude
        const lhaM = ((gmst + lon - moonRa) % 360 + 360) % 360 * this.rad;
        const sinAltM = sinLat * sinDecM + cosLat * cosDecM * Math.cos(lhaM);
        const altM = Math.asin(Math.max(-1, Math.min(1, sinAltM))) * this.deg - 0.95 * Math.cos(Math.max(0, sinAltM));

        const bVal = (this.bortleOverride !== 'auto') ? effBortle : (this.bortleArray[rowOffset + c] || 2);
        const baseScore = Math.max(1.0, Math.min(10.0, 10.0 - (bVal - 1.0) * 1.1));

        let moonPen = 0;
        if (altM > 0) {
          moonPen = moonIllum * Math.sqrt(Math.sin(altM * this.rad)) * 7.0;
        }

        let sunFac = 1.0;
        if (altS > -12.0) {
          sunFac = (altS - (-6.0)) / (-12.0 - (-6.0));
        }

        const sc = Math.max(0.0, Math.min(10.0, (baseScore - moonPen) * sunFac));
        const twilightFeather = Math.min(1.0, (-6.0 - altS) / 4.0);
        const alpha = Math.round(255 * twilightFeather);

        const { red, green, blue } = this.getDarkSkiesRgb(sc);
        data32[rowOffset + c] = (alpha << 24) | (blue << 16) | (green << 8) | red;
      }
    }

    this.mapCtx.putImageData(imgData, 0, 0);

    const coords: [number, number][] = [
      [bounds.west, bounds.north],
      [bounds.east, bounds.north],
      [bounds.east, bounds.south],
      [bounds.west, bounds.south]
    ];

    const effectiveOpacity = this.mapOpacity * (store.getState().weatherOpacity !== undefined ? store.getState().weatherOpacity : 1.0);

    const src = this.map.getSource('ephem-darkskies-source');
    if (src) {
      src.updateImage({
        url: this.mapCanvas.toDataURL(),
        coordinates: coords
      });
    } else {
      this.map.addSource('ephem-darkskies-source', {
        type: 'image',
        url: this.mapCanvas.toDataURL(),
        coordinates: coords
      });

      const beforeLayer = this.map.getLayer('hiking-trails-casing') ? 'hiking-trails-casing' : undefined;
      this.map.addLayer({
        id: 'ephem-darkskies-layer',
        type: 'raster',
        source: 'ephem-darkskies-source',
        paint: {
          'raster-opacity': effectiveOpacity,
          'raster-resampling': 'linear'
        }
      }, beforeLayer);
    }

    if (this.map.getLayer('ephem-darkskies-layer')) {
      this.map.setLayoutProperty('ephem-darkskies-layer', 'visibility', this.overlayEnabled ? 'visible' : 'none');
      this.map.setPaintProperty('ephem-darkskies-layer', 'raster-opacity', effectiveOpacity);
    }

    if (this.map.getLayer('ephem-bortle-base-layer')) {
      this.map.setLayoutProperty('ephem-bortle-base-layer', 'visibility', this.bortleBaseEnabled ? 'visible' : 'none');
    }
  }

  // --- Rendering UI ---

  public render(state: AppState): void {
    if (!this.container) return;

    const poi = state.selectedPoi;
    const lat = poi ? poi.lat : state.mapCenter[1];
    const lon = poi ? poi.lng : state.mapCenter[0];

    const locKey = `${lat.toFixed(3)}_${lon.toFixed(3)}`;
    if (locKey !== this.lastPoiKey) {
      this.lastPoiKey = locKey;
      getBortleAt(lat, lon).then(bVal => {
        if (bVal > 0 && bVal !== this.currentBortle) {
          this.currentBortle = bVal;
          this.metricsCache.clear();
          this.render(store.getState());
          this.updateMapLayer();
        }
      });
    }

    const effBortle = this.getEffectiveBortle();
    const daysInMonth = new Date(this.currentYear, this.currentMonth + 1, 0).getDate();
    const firstDayIndex = new Date(this.currentYear, this.currentMonth, 1).getDay(); // 0 = Sun
    const monthName = new Date(this.currentYear, this.currentMonth, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

    // Ensure metrics cached for every day of the month
    const dayMetricsList: DailyDarknessMetrics[] = [];
    for (let d = 1; d <= daysInMonth; d++) {
      const dStr = `${this.currentYear}-${String(this.currentMonth + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      let m = this.metricsCache.get(dStr);
      if (!m) {
        const dObj = new Date(this.currentYear, this.currentMonth, d, 12, 0, 0);
        m = this.calculateDailyDarkness(dObj, lat, lon, effBortle);
        this.metricsCache.set(dStr, m);
      }
      dayMetricsList.push(m);
    }

    const selDateStr = `${this.selectedDate.getFullYear()}-${String(this.selectedDate.getMonth() + 1).padStart(2, '0')}-${String(this.selectedDate.getDate()).padStart(2, '0')}`;
    let activeMetrics = this.metricsCache.get(selDateStr) || dayMetricsList[0];

    // Build Calendar Grid HTML
    let cellsHtml = '';
    for (let i = 0; i < firstDayIndex; i++) {
      cellsHtml += `<div class="ephem-cell blank" style="visibility: hidden;"></div>`;
    }

    dayMetricsList.forEach((m, idx) => {
      const dNum = idx + 1;
      const isSel = (m.dateStr === selDateStr);
      const isToday = (m.dateStr === new Date().toISOString().split('T')[0]);

      let badgeHtml = '';
      if (m.phaseInfo.name.includes('Full') && m.phaseInfo.fraction >= 0.98) {
        badgeHtml = `<span class="ephem-badge-event super" title="Full Moon">🌕</span>`;
      } else if (m.phaseInfo.name.includes('New') && m.phaseInfo.fraction <= 0.03) {
        badgeHtml = `<span class="ephem-badge-event moonless" title="New Moon • Prime Pitch Black">🌑</span>`;
      } else if (m.darkHours >= 6.0) {
        badgeHtml = `<span class="ephem-badge-event moonless" title="${m.darkHours}h of Pure Darkness">🌌</span>`;
      }

      // Mini inline SVG 24h diurnal curve
      let svgPolyPoints = '';
      if (m.hourlyScores && m.hourlyScores.length === 24) {
        svgPolyPoints = m.hourlyScores.map((sc, h) => {
          const sx = (h / 23) * 60;
          const sy = 12 - (sc / 10.0) * 11;
          return `${sx.toFixed(1)},${sy.toFixed(1)}`;
        }).join(' ');
      }

      const scoreColor = this.getDarkSkiesHex(m.maxScore);

      cellsHtml += `
        <div class="ephem-cell ${isSel ? 'selected' : ''} ${isToday ? 'today' : ''}" data-day="${dNum}">
          <div class="ephem-cell-top">
            <span class="ephem-cell-num">${dNum}</span>
            <span class="ephem-cell-moon" title="${m.phaseInfo.name} (${m.phaseInfo.percent})">${m.phaseInfo.icon}</span>
          </div>
          ${badgeHtml}
          ${svgPolyPoints ? `
            <svg viewBox="0 0 60 12" class="ephem-cell-svg" preserveAspectRatio="none">
              <polyline points="${svgPolyPoints}" fill="none" stroke="${scoreColor}" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          ` : ''}
          <div class="ephem-cell-bottom">
            <span class="ephem-cell-score" style="background: ${scoreColor};">★ ${m.maxScore.toFixed(1)}</span>
            <span class="ephem-cell-darkhours" title="${m.darkHours} hrs of pitch-black skies">🌑 ${m.darkHours.toFixed(1)}h</span>
          </div>
        </div>
      `;
    });

    // Formatting astro times for activeMetrics
    const fmtT = (d?: Date | null) => (!d || isNaN(d.getTime()) ? '--:--' : d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false }));
    const sTimes = activeMetrics.sTimes;
    const mTimes = activeMetrics.mTimes;

    const sunrise = sTimes ? fmtT(sTimes.sunrise) : '--:--';
    const sunset = sTimes ? fmtT(sTimes.sunset) : '--:--';
    const goldenHour = sTimes ? `${fmtT(sTimes.goldenHour)} - ${fmtT(sTimes.sunset)}` : '--';
    const blueHour = sTimes ? `${fmtT(sTimes.sunset)} - ${fmtT(sTimes.dusk)}` : '--';
    const moonrise = mTimes ? fmtT(mTimes.rise) : '--:--';
    const moonset = mTimes ? fmtT(mTimes.set) : '--:--';

    let pitchBlackStr = 'None (moon or sun illumination present all night)';
    if (activeMetrics.darkStartMs && activeMetrics.darkEndMs && activeMetrics.darkHours > 0) {
      const sT = new Date(activeMetrics.darkStartMs).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
      const eT = new Date(activeMetrics.darkEndMs).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
      pitchBlackStr = `${sT} – ${eT} (${activeMetrics.darkHours.toFixed(1)} hrs of pitch black)`;
    }

    const curHourScore = (activeMetrics.hourlyScores && activeMetrics.hourlyScores[this.selectedHour] !== undefined)
      ? activeMetrics.hourlyScores[this.selectedHour]
      : 0;

    const activeAstro = this.getCelestial(new Date(this.selectedDate.getFullYear(), this.selectedDate.getMonth(), this.selectedDate.getDate(), this.selectedHour, 0, 0));
    const curSunAlt = this.altitude(lat, lon, activeAstro.gmst, activeAstro.sun.ra, activeAstro.sun.dec);
    const curMoonAlt = this.moonAltitude(lat, lon, activeAstro.gmst, activeAstro.moon.ra, activeAstro.moon.dec);

    let celestialStatus = '☀️ Daylight / Twilight';
    if (curSunAlt < -18) {
      if (curMoonAlt <= 0 || activeAstro.moon.fraction <= 0.03) celestialStatus = '🌌 Pristine Astronomical Dark';
      else celestialStatus = `🌙 Moonlit Night (${Math.round(activeAstro.moon.fraction * 100)}% illum)`;
    }

    const monthsFromBase = (this.currentYear - this.baseYear) * 12 + (this.currentMonth - this.baseMonth);
    const canPrev = monthsFromBase > -1;
    const canNext = monthsFromBase < 11;

    // Assembly
    this.container.innerHTML = `
      <!-- Top Bar: Header, Methodology Disclaimer Note, Map Controls, Legend, Minimize & Close -->
      <div class="ephem-dock-top-bar">
        <div class="ephem-top-bar-left">
          <div style="display: flex; align-items: center; gap: 8px;">
            <span class="ephem-top-icon">🌗</span>
            <div style="display: flex; flex-direction: column;">
              <span class="ephem-top-title">Solar & Lunar Ephemeris • Dark Skies Calendar</span>
              <span class="ephem-top-poi-pill">📍 ${lat.toFixed(3)}°N, ${Math.abs(lon).toFixed(3)}°W (Bortle ${effBortle})</span>
            </div>
          </div>
          <!-- Methodology Disclaimer Note -->
          <div class="ephem-disclaimer-badge" title="Algorithm Details">
            <span style="font-size: 0.85rem;">ℹ️</span>
            <span>Outlook based on static Bortle and solar/lunar phases, no atmospheric data. Intended for long term outlook/planning.</span>
          </div>
        </div>

        <!-- Center: Map Controls & Legend -->
        <div class="ephem-top-bar-center">
          <div class="ephem-dock-map-controls">
            <label class="ephem-dock-chk-label" title="Enable or disable the underlying Bortle light pollution basemap">
              <input type="checkbox" id="ephem-dock-bortle-basemap-toggle" ${this.bortleBaseEnabled ? 'checked' : ''}>
              <span>Bortle Basemap</span>
            </label>
            <label class="ephem-dock-chk-label" title="Enable or disable the dynamic darkness forecast overlay on the map">
              <input type="checkbox" id="ephem-dock-map-toggle" ${this.overlayEnabled ? 'checked' : ''}>
              <span>Darkness Overlay</span>
            </label>
            <div class="ephem-dock-opacity-row" title="Adjust darkness overlay opacity (default 80%)">
              <span style="font-size: 0.70rem; color: #cbd5e1; font-weight: 600;">Opacity</span>
              <input type="range" id="ephem-dock-opacity-slider" min="0" max="100" value="${Math.round(this.mapOpacity * 100)}" style="width: 70px; accent-color: #38bdf8; cursor: pointer;">
              <span id="ephem-dock-opacity-val" style="min-width: 32px; font-family: monospace; font-size: 0.72rem; color: #38bdf8; font-weight: 700;">${Math.round(this.mapOpacity * 100)}%</span>
            </div>
            <div style="display: flex; align-items: center; gap: 4px;" title="Override Bortle class at current location">
              <span style="font-size: 0.70rem; color: #cbd5e1; font-weight: 600;">Bortle:</span>
              <select id="ephem-dock-bortle-override" style="padding: 2px 5px; font-size: 0.70rem; border-radius: 4px; background: rgba(0,0,0,0.6); color: #e2e8f0; border: 1px solid rgba(255,255,255,0.2);">
                <option value="auto" ${this.bortleOverride === 'auto' ? 'selected' : ''}>Auto (${this.currentBortle})</option>
                ${[1, 2, 3, 4, 5, 6, 7, 8, 9].map(b => `<option value="${b}" ${this.bortleOverride === String(b) ? 'selected' : ''}>Class ${b}</option>`).join('')}
              </select>
            </div>
          </div>

          <!-- Color Scale Legend -->
          <div class="ephem-dock-legend-container" title="Dark Skies Quality Score Scale">
            <div class="ephem-legend-gradient-bar"></div>
            <div class="ephem-legend-ticks">
              <span>0 Red</span><span>2 Orange</span><span>4 Yellow</span><span>6 Green</span><span>8 Cyan</span><span>10 Blue</span>
            </div>
          </div>
        </div>

        <!-- Right: Collapse & Close -->
        <div class="ephem-top-bar-right">
          <button class="ephem-dock-btn" id="btn-ephem-collapse" title="${this.isCollapsed ? 'Expand Dock' : 'Collapse Dock'}">
            <span>${this.isCollapsed ? '▲' : '▼'}</span> <span>${this.isCollapsed ? 'Expand' : 'Minimize'}</span>
          </button>
          <button class="ephem-dock-close-btn" id="btn-ephem-close" title="Close Ephemeris Dock">
            ✕ Close
          </button>
        </div>
      </div>

      <!-- Dock Body (Collapsible) -->
      <div class="ephem-dock-body" id="ephem-dock-body" style="${this.isCollapsed ? 'display: none;' : ''}">
        <div class="ephem-dock-content">
          <!-- Left Column: Astro Details & 24h Diurnal Curve -->
          <div class="ephem-dock-astro-col">
            <div style="display: flex; justify-content: space-between; align-items: center; border-bottom: 1px solid rgba(255,255,255,0.1); padding-bottom: 5px;">
              <span style="font-weight: 800; font-size: 0.88rem; color: #f8fafc;">
                🗓️ ${this.selectedDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
              </span>
              <span class="ephem-cell-score" style="font-size: 0.78rem; font-weight: 800; padding: 2px 8px; border-radius: 4px; background: ${this.getDarkSkiesHex(activeMetrics.maxScore)}; color: #ffffff;">
                Max: ${activeMetrics.maxScore.toFixed(1)} / 10
              </span>
            </div>

            <div class="ephem-astro-grid">
              <div class="ephem-astro-card">
                <div style="color: #f97316; font-weight: 700; margin-bottom: 2px;">🌅 Sun Times</div>
                <div>Sunrise: <strong>${sunrise}</strong></div>
                <div>Sunset: <strong>${sunset}</strong></div>
                <div>Golden: <strong>${goldenHour}</strong></div>
                <div>Blue: <strong>${blueHour}</strong></div>
              </div>
              <div class="ephem-astro-card">
                <div style="color: #38bdf8; font-weight: 700; margin-bottom: 2px;">🌙 Moon Times</div>
                <div>Moonrise: <strong>${moonrise}</strong></div>
                <div>Moonset: <strong>${moonset}</strong></div>
                <div>Phase: <strong>${activeMetrics.phaseInfo.icon} ${activeMetrics.phaseInfo.name}</strong></div>
                <div>Illumination: <strong>${activeMetrics.phaseInfo.percent}</strong></div>
              </div>
            </div>

            <!-- Pitch Black Window Card -->
            <div style="background: rgba(0, 50, 255, 0.12); border: 1px solid rgba(56, 189, 248, 0.35); border-radius: 6px; padding: 5px 8px; font-size: 0.70rem;">
              <div style="color: #38bdf8; font-weight: 700;">🌌 Prime Astronomical Darkness Window</div>
              <div style="color: #ffffff; margin-top: 1px;">${pitchBlackStr}</div>
            </div>

            <!-- 24-Hour Diurnal Darkness Curve (Sparkline) -->
            <div style="display: flex; flex-direction: column; gap: 4px; margin-top: 2px;">
              <div style="display: flex; justify-content: space-between; font-size: 0.68rem; color: #94a3b8;">
                <span>24h Diurnal Curve (Click to Scrub Hour)</span>
                <span id="ephem-current-hour-score" style="color: #38bdf8; font-weight: 700;">At ${String(this.selectedHour).padStart(2, '0')}:00: ${curHourScore.toFixed(1)} / 10</span>
              </div>
              <canvas id="ephem-diurnal-canvas" width="320" height="46" style="width: 100%; height: 46px; border-radius: 6px; background: rgba(0,0,0,0.4); border: 1px solid rgba(255,255,255,0.08); cursor: pointer;"></canvas>
            </div>
          </div>

          <!-- Right Column: 7-Column Calendar Grid -->
          <div class="ephem-dock-calendar-col">
            <div class="ephem-cal-month-nav">
              <button class="ephem-month-nav-btn" id="btn-ephem-prev-month" ${!canPrev ? 'disabled' : ''}>◀ Prev</button>
              <span class="ephem-cal-month-title">📅 ${monthName}</span>
              <button class="ephem-month-nav-btn" id="btn-ephem-next-month" ${!canNext ? 'disabled' : ''}>Next ▶</button>
            </div>
            <div class="ephem-grid-header">
              <div>SUN</div><div>MON</div><div>TUE</div><div>WED</div><div>THU</div><div>FRI</div><div>SAT</div>
            </div>
            <div class="ephem-grid-body" id="ephem-calendar-cells">
              ${cellsHtml}
            </div>
          </div>
        </div>

        <!-- 🌌 Continuous Monthly Diurnal Darkness Ribbon (Full Month 744h Outlook) -->
        <div class="ephem-month-curve-strip">
          <div class="ephem-month-curve-header">
            <div style="display: flex; align-items: center; gap: 8px;">
              <span style="font-size: 0.82rem; font-weight: 800; color: #f8fafc;">📈 Full Month Continuous Diurnal Darkness Outlook</span>
              <span style="font-size: 0.68rem; color: #94a3b8;">Continuous 744-Hour Dark Skies Wave across full month • Hover or click to scrub any hour</span>
            </div>
            <div style="font-size: 0.72rem; color: #38bdf8; font-family: monospace; font-weight: 600;" id="ephem-month-curve-hover-val">
              Click / drag anywhere on curve to scrub date & hour
            </div>
          </div>
          <div class="ephem-month-curve-canvas-container" style="position: relative; width: 100%; height: 75px; border-radius: 6px; overflow: hidden; background: rgba(0,0,0,0.5); border: 1px solid rgba(255,255,255,0.12);">
            <canvas id="ephem-month-curve-canvas" style="width: 100%; height: 75px; display: block; cursor: crosshair;"></canvas>
          </div>
        </div>

        <!-- Dock Integrated Timeline Scrubber -->
        <div class="ephem-dock-timeline-strip">
          <div class="timeline-controls" style="margin: 0; display: flex; align-items: center; gap: 4px;">
            <button class="timeline-btn" id="btn-ephem-jump-0" title="Midnight (00:00)">⏮</button>
            <button class="timeline-btn" id="btn-ephem-step-back" title="Previous Hour">◀</button>
            <button class="timeline-btn play-btn ${this.isPlaying ? 'playing' : ''}" id="btn-ephem-play" title="Play / Pause 24h Cycle">
              ${this.isPlaying ? '⏸' : '▶'}
            </button>
            <button class="timeline-btn" id="btn-ephem-step-fwd" title="Next Hour">▶</button>
            <button class="timeline-btn" id="btn-ephem-jump-23" title="Hour 23 (23:00)">⏭</button>
            <div style="width: 1px; height: 16px; background: rgba(255,255,255,0.15); margin: 0 4px;"></div>
            <button class="timeline-btn-text" id="btn-ephem-speed" title="Playback Speed">${this.playbackSpeed.toFixed(1)}x</button>
            <button class="timeline-btn-text ${this.isLooping ? 'active' : ''}" id="btn-ephem-loop" title="Loop 24h">🔁 Loop</button>
          </div>

          <div style="flex: 1; display: flex; flex-direction: column; gap: 2px; min-width: 250px;">
            <div style="display: flex; justify-content: space-between; font-size: 0.65rem; color: #94a3b8; font-family: monospace;">
              <span>00:00</span>
              <span id="ephem-slider-hour-badge" style="color: #38bdf8; font-weight: 700; font-size: 0.72rem;">${String(this.selectedHour).padStart(2, '0')}:00 Local</span>
              <span>23:00</span>
            </div>
            <input type="range" id="ephem-timeline-slider" min="0" max="23" value="${this.selectedHour}" style="width: 100%; accent-color: #38bdf8; cursor: pointer;">
          </div>

          <div class="ephem-dock-celestial-readout" style="display: flex; align-items: center; gap: 8px; font-size: 0.70rem; color: #cbd5e1; font-family: monospace; white-space: nowrap;">
            <span>☀️ Sun: <strong style="color: ${curSunAlt < -18 ? '#38bdf8' : '#f59e0b'};">${curSunAlt.toFixed(1)}°</strong></span>
            <span>🌙 Moon: <strong style="color: #e2e8f0;">${curMoonAlt.toFixed(1)}°</strong></span>
            <span class="ephem-celestial-badge" style="background: rgba(56, 189, 248, 0.15); color: #38bdf8; border: 1px solid rgba(56, 189, 248, 0.3); padding: 1px 6px; border-radius: 4px; font-weight: 700;">
              ${celestialStatus}
            </span>
          </div>
        </div>
      </div>
    `;

    this.bindEvents();
    this.drawDiurnalSparkline(activeMetrics.hourlyScores);
    this.drawMonthlyDiurnalCurve();
  }

  // --- Drawing Sparkline & Monthly Curve ---

  private drawDiurnalSparkline(scores: number[]): void {
    const canvas = document.getElementById('ephem-diurnal-canvas') as HTMLCanvasElement;
    if (!canvas || !scores || scores.length !== 24) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const w = canvas.width, h = canvas.height;
    ctx.clearRect(0, 0, w, h);

    const stepX = w / 23;
    const pad = 4;
    const plotH = h - pad * 2;

    // Background horizontal 5.0 guide
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.08)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(0, h - pad - 0.5 * plotH);
    ctx.lineTo(w, h - pad - 0.5 * plotH);
    ctx.stroke();

    // Curve Path
    ctx.beginPath();
    for (let i = 0; i < 24; i++) {
      const sc = scores[i];
      const x = i * stepX;
      const y = h - pad - (sc / 10.0) * plotH;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.strokeStyle = '#38bdf8';
    ctx.lineWidth = 2.0;
    ctx.stroke();

    // Area Gradient Fill
    ctx.lineTo(w, h);
    ctx.lineTo(0, h);
    ctx.closePath();
    const grad = ctx.createLinearGradient(0, pad, 0, h - pad);
    grad.addColorStop(0, 'rgba(56, 189, 248, 0.35)');
    grad.addColorStop(1, 'rgba(56, 189, 248, 0.02)');
    ctx.fillStyle = grad;
    ctx.fill();

    // Selected Hour needle & circle
    const selX = this.selectedHour * stepX;
    const selScore = scores[this.selectedHour];
    const selY = h - pad - (selScore / 10.0) * plotH;

    ctx.strokeStyle = '#f59e0b';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(selX, pad);
    ctx.lineTo(selX, h - pad);
    ctx.stroke();

    ctx.fillStyle = '#f59e0b';
    ctx.beginPath();
    ctx.arc(selX, selY, 4, 0, Math.PI * 2);
    ctx.fill();
  }

  private drawMonthlyDiurnalCurve(): void {
    const canvas = document.getElementById('ephem-month-curve-canvas') as HTMLCanvasElement;
    if (!canvas) return;

    const container = canvas.parentElement;
    const width = container ? container.clientWidth : 1000;
    const height = 75;

    if (canvas.width !== width || canvas.height !== height) {
      canvas.width = width;
      canvas.height = height;
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const w = canvas.width, h = canvas.height;
    ctx.clearRect(0, 0, w, h);

    const daysInMonth = new Date(this.currentYear, this.currentMonth + 1, 0).getDate();
    const totalHours = daysInMonth * 24;

    const monthPoints: Array<{ day: number; hour: number; score: number }> = [];
    const dayMetricsList: DailyDarknessMetrics[] = [];

    const effBortle = this.getEffectiveBortle();
    const lat = store.getState().selectedPoi ? store.getState().selectedPoi!.lat : store.getState().mapCenter[1];
    const lon = store.getState().selectedPoi ? store.getState().selectedPoi!.lng : store.getState().mapCenter[0];

    for (let d = 1; d <= daysInMonth; d++) {
      const dStr = `${this.currentYear}-${String(this.currentMonth + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      let m = this.metricsCache.get(dStr);
      if (!m) {
        m = this.calculateDailyDarkness(new Date(this.currentYear, this.currentMonth, d, 12, 0, 0), lat, lon, effBortle);
        this.metricsCache.set(dStr, m);
      }
      dayMetricsList.push(m);
      for (let hr = 0; hr < 24; hr++) {
        monthPoints.push({
          day: d,
          hour: hr,
          score: (m.hourlyScores && m.hourlyScores[hr] !== undefined) ? m.hourlyScores[hr] : 0
        });
      }
    }

    const topPad = 18;
    const botPad = 4;
    const plotH = h - topPad - botPad;
    const stepX = w / totalHours;

    // 1. Day separator lines & weekend shading & labels
    for (let d = 1; d <= daysInMonth; d++) {
      const dDate = new Date(this.currentYear, this.currentMonth, d);
      const dayOfWeek = dDate.getDay(); // 0 = Sun, 6 = Sat
      const dayLeft = (d - 1) * 24 * stepX;
      const dayWidth = 24 * stepX;

      if (dayOfWeek === 0 || dayOfWeek === 6) {
        ctx.fillStyle = 'rgba(56, 189, 248, 0.04)';
        ctx.fillRect(dayLeft, topPad, dayWidth, plotH);
      }

      ctx.strokeStyle = 'rgba(255, 255, 255, 0.08)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(dayLeft, 0);
      ctx.lineTo(dayLeft, h);
      ctx.stroke();

      const isSel = (d === this.selectedDate.getDate());
      ctx.fillStyle = isSel ? '#38bdf8' : 'rgba(255, 255, 255, 0.65)';
      ctx.font = isSel ? 'bold 9px monospace' : '8px monospace';
      ctx.textAlign = 'center';
      ctx.fillText(String(d), dayLeft + dayWidth * 0.5, 9);

      const m = dayMetricsList[d - 1];
      if (m && (d === 1 || d % 4 === 0 || d === daysInMonth || m.phaseInfo.name.includes('New') || m.phaseInfo.name.includes('Full'))) {
        ctx.font = '8px sans-serif';
        ctx.fillText(m.phaseInfo.icon, dayLeft + dayWidth * 0.5, topPad - 2);
      }
    }

    // 2. Score 5.0 horizontal guide line
    const y5 = h - botPad - 0.5 * plotH;
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.06)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(0, y5);
    ctx.lineTo(w, y5);
    ctx.stroke();

    // 3. Highlight selected day bracket
    const selDay = this.selectedDate.getDate();
    const selLeft = (selDay - 1) * 24 * stepX;
    const selWidth = 24 * stepX;
    ctx.fillStyle = 'rgba(0, 50, 255, 0.15)';
    ctx.fillRect(selLeft, topPad, selWidth, plotH);
    ctx.strokeStyle = 'rgba(56, 189, 248, 0.7)';
    ctx.lineWidth = 1.5;
    ctx.strokeRect(selLeft, topPad, selWidth, plotH);

    // 4. Continuous score curve
    ctx.beginPath();
    for (let i = 0; i < totalHours; i++) {
      const sc = monthPoints[i].score;
      const x = i * stepX + stepX * 0.5;
      const y = h - botPad - (sc / 10.0) * plotH;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.strokeStyle = '#38bdf8';
    ctx.lineWidth = 1.8;
    ctx.stroke();

    // 5. Area fill with production dark skies color gradient
    ctx.lineTo(w, h - botPad);
    ctx.lineTo(0, h - botPad);
    ctx.closePath();

    const fillGrad = ctx.createLinearGradient(0, topPad, 0, h - botPad);
    fillGrad.addColorStop(0.0, 'rgba(0, 50, 255, 0.45)');   // Deep Blue (Score 10)
    fillGrad.addColorStop(0.3, 'rgba(0, 200, 200, 0.30)');  // Cyan (Score 7-8)
    fillGrad.addColorStop(0.6, 'rgba(34, 197, 94, 0.20)');  // Green (Score 4-5)
    fillGrad.addColorStop(0.85, 'rgba(245, 158, 11, 0.10)');// Orange (Score 2)
    fillGrad.addColorStop(1.0, 'rgba(180, 0, 0, 0.03)');    // Red (Score 0)
    ctx.fillStyle = fillGrad;
    ctx.fill();

    // 6. Active selected hour needle & circle
    const curTotalHour = (selDay - 1) * 24 + this.selectedHour;
    const needleX = curTotalHour * stepX + stepX * 0.5;
    const curScore = monthPoints[curTotalHour] ? monthPoints[curTotalHour].score : 0;
    const needleY = h - botPad - (curScore / 10.0) * plotH;

    ctx.strokeStyle = '#f59e0b';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(needleX, topPad);
    ctx.lineTo(needleX, h - botPad);
    ctx.stroke();

    ctx.fillStyle = '#f59e0b';
    ctx.beginPath();
    ctx.arc(needleX, needleY, 3.5, 0, Math.PI * 2);
    ctx.fill();
  }

  // --- Event Handling & Interactions ---

  private bindEvents(): void {
    if (!this.container) return;

    // Minimize / Collapse
    const collapseBtn = this.container.querySelector('#btn-ephem-collapse');
    if (collapseBtn) {
      collapseBtn.addEventListener('click', () => {
        this.isCollapsed = !this.isCollapsed;
        const body = this.container?.querySelector('#ephem-dock-body') as HTMLElement;
        if (body) body.style.display = this.isCollapsed ? 'none' : '';
        const iconSpan = collapseBtn.querySelector('span:first-child');
        const textSpan = collapseBtn.querySelector('span:last-child');
        if (iconSpan) iconSpan.textContent = this.isCollapsed ? '▲' : '▼';
        if (textSpan) textSpan.textContent = this.isCollapsed ? 'Expand' : 'Minimize';
        if (!this.isCollapsed) this.drawMonthlyDiurnalCurve();
      });
    }

    // Close Button
    const closeBtn = this.container.querySelector('#btn-ephem-close');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => this.close());
    }

    // Month Navigation
    const prevBtn = this.container.querySelector('#btn-ephem-prev-month');
    if (prevBtn) {
      prevBtn.addEventListener('click', () => {
        this.currentMonth--;
        if (this.currentMonth < 0) {
          this.currentMonth = 11;
          this.currentYear--;
        }
        this.selectedDate = new Date(this.currentYear, this.currentMonth, Math.min(this.selectedDate.getDate(), new Date(this.currentYear, this.currentMonth + 1, 0).getDate()));
        this.render(store.getState());
        this.updateMapLayer();
      });
    }

    const nextBtn = this.container.querySelector('#btn-ephem-next-month');
    if (nextBtn) {
      nextBtn.addEventListener('click', () => {
        this.currentMonth++;
        if (this.currentMonth > 11) {
          this.currentMonth = 0;
          this.currentYear++;
        }
        this.selectedDate = new Date(this.currentYear, this.currentMonth, Math.min(this.selectedDate.getDate(), new Date(this.currentYear, this.currentMonth + 1, 0).getDate()));
        this.render(store.getState());
        this.updateMapLayer();
      });
    }

    // Calendar Day Cell Click
    this.container.querySelectorAll('.ephem-cell:not(.blank)').forEach(cell => {
      cell.addEventListener('click', () => {
        const dNum = parseInt((cell as HTMLElement).dataset.day || '1', 10);
        this.selectedDate = new Date(this.currentYear, this.currentMonth, dNum);
        this.render(store.getState());
        this.updateMapLayer();
      });
    });

    // Bortle Basemap Toggle
    const bortleChk = this.container.querySelector('#ephem-dock-bortle-basemap-toggle') as HTMLInputElement;
    if (bortleChk) {
      bortleChk.addEventListener('change', () => {
        this.bortleBaseEnabled = bortleChk.checked;
        if (this.map && this.map.getLayer('ephem-bortle-base-layer')) {
          this.map.setLayoutProperty('ephem-bortle-base-layer', 'visibility', this.bortleBaseEnabled ? 'visible' : 'none');
        }
      });
    }

    // Darkness Overlay Toggle
    const overlayChk = this.container.querySelector('#ephem-dock-map-toggle') as HTMLInputElement;
    if (overlayChk) {
      overlayChk.addEventListener('change', () => {
        this.overlayEnabled = overlayChk.checked;
        if (this.map && this.map.getLayer('ephem-darkskies-layer')) {
          this.map.setLayoutProperty('ephem-darkskies-layer', 'visibility', this.overlayEnabled ? 'visible' : 'none');
        }
        if (this.overlayEnabled) this.updateMapLayer();
      });
    }

    // Opacity Slider (defaults to 80%)
    const opacitySlider = this.container.querySelector('#ephem-dock-opacity-slider') as HTMLInputElement;
    const opacityVal = this.container.querySelector('#ephem-dock-opacity-val');
    if (opacitySlider) {
      opacitySlider.addEventListener('input', () => {
        this.mapOpacity = parseInt(opacitySlider.value, 10) / 100.0;
        if (opacityVal) opacityVal.textContent = `${Math.round(this.mapOpacity * 100)}%`;
        const effectiveOpacity = this.mapOpacity * (store.getState().weatherOpacity !== undefined ? store.getState().weatherOpacity : 1.0);
        if (this.map && this.map.getLayer('ephem-darkskies-layer')) {
          this.map.setPaintProperty('ephem-darkskies-layer', 'raster-opacity', effectiveOpacity);
        }
      });
    }

    // Bortle Class Override
    const bortleSelect = this.container.querySelector('#ephem-dock-bortle-override') as HTMLSelectElement;
    if (bortleSelect) {
      bortleSelect.addEventListener('change', () => {
        this.bortleOverride = bortleSelect.value;
        this.metricsCache.clear();
        this.render(store.getState());
        this.updateMapLayer();
      });
    }

    // Timeline Slider
    const timelineSlider = this.container.querySelector('#ephem-timeline-slider') as HTMLInputElement;
    if (timelineSlider) {
      timelineSlider.addEventListener('input', () => {
        this.selectedHour = parseInt(timelineSlider.value, 10);
        this.updateHourUI();
        this.updateMapLayer();
      });
    }

    // 24h Diurnal Sparkline Click
    const diurnalCanvas = this.container.querySelector('#ephem-diurnal-canvas') as HTMLCanvasElement;
    if (diurnalCanvas) {
      diurnalCanvas.addEventListener('click', (e) => {
        const rect = diurnalCanvas.getBoundingClientRect();
        const clickX = e.clientX - rect.left;
        const ratio = Math.max(0, Math.min(1, clickX / rect.width));
        this.selectedHour = Math.min(23, Math.round(ratio * 23));
        this.updateHourUI();
        this.updateMapLayer();
      });
    }

    // 🌌 Monthly Continuous Ribbon Hover & Scrub
    const monthCanvas = this.container.querySelector('#ephem-month-curve-canvas') as HTMLCanvasElement;
    if (monthCanvas) {
      let isScrubbing = false;
      const daysInMonth = new Date(this.currentYear, this.currentMonth + 1, 0).getDate();
      const totalHours = daysInMonth * 24;

      const handleMonthScrub = (e: PointerEvent) => {
        const rect = monthCanvas.getBoundingClientRect();
        const clickX = e.clientX - rect.left;
        const ratio = Math.max(0, Math.min(1, clickX / rect.width));
        const totalH = Math.min(totalHours - 1, Math.floor(ratio * totalHours));
        const targetDay = Math.floor(totalH / 24) + 1;
        const targetHour = totalH % 24;

        this.selectedDate = new Date(this.currentYear, this.currentMonth, targetDay);
        this.selectedHour = targetHour;
        this.render(store.getState());
        this.updateMapLayer();
      };

      monthCanvas.addEventListener('pointerdown', (e) => {
        isScrubbing = true;
        try { monthCanvas.setPointerCapture(e.pointerId); } catch (_) {}
        handleMonthScrub(e);
      });

      monthCanvas.addEventListener('pointermove', (e) => {
        const rect = monthCanvas.getBoundingClientRect();
        const clickX = e.clientX - rect.left;
        const ratio = Math.max(0, Math.min(1, clickX / rect.width));
        const totalH = Math.min(totalHours - 1, Math.floor(ratio * totalHours));
        const targetDay = Math.floor(totalH / 24) + 1;
        const targetHour = totalH % 24;

        const dStr = `${this.currentYear}-${String(this.currentMonth + 1).padStart(2, '0')}-${String(targetDay).padStart(2, '0')}`;
        const m = this.metricsCache.get(dStr);
        const sc = (m && m.hourlyScores && m.hourlyScores[targetHour] !== undefined) ? m.hourlyScores[targetHour] : 0;
        const hoverEl = this.container?.querySelector('#ephem-month-curve-hover-val');
        if (hoverEl) {
          const monthShort = new Date(this.currentYear, this.currentMonth, 1).toLocaleDateString('en-US', { month: 'short' });
          hoverEl.innerHTML = `🗓️ <strong>${monthShort} ${targetDay} • ${String(targetHour).padStart(2, '0')}:00</strong> | Darkness: <strong style="color: #38bdf8;">${sc.toFixed(1)} / 10</strong> ${m ? '(' + m.phaseInfo.icon + ' ' + m.phaseInfo.name + ')' : ''}`;
        }

        if (isScrubbing) handleMonthScrub(e);
      });

      monthCanvas.addEventListener('pointerup', (e) => {
        isScrubbing = false;
        try { monthCanvas.releasePointerCapture(e.pointerId); } catch (_) {}
      });

      monthCanvas.addEventListener('pointerleave', () => {
        if (!isScrubbing) {
          const hoverEl = this.container?.querySelector('#ephem-month-curve-hover-val');
          if (hoverEl) hoverEl.textContent = 'Click / drag anywhere on curve to scrub date & hour';
        }
      });
    }

    // Playback Controls
    const jump0Btn = this.container.querySelector('#btn-ephem-jump-0');
    if (jump0Btn) jump0Btn.addEventListener('click', () => this.setHour(0));

    const jump23Btn = this.container.querySelector('#btn-ephem-jump-23');
    if (jump23Btn) jump23Btn.addEventListener('click', () => this.setHour(23));

    const stepBackBtn = this.container.querySelector('#btn-ephem-step-back');
    if (stepBackBtn) stepBackBtn.addEventListener('click', () => this.stepHour(-1));

    const stepFwdBtn = this.container.querySelector('#btn-ephem-step-fwd');
    if (stepFwdBtn) stepFwdBtn.addEventListener('click', () => this.stepHour(1));

    const playBtn = this.container.querySelector('#btn-ephem-play');
    if (playBtn) playBtn.addEventListener('click', () => this.togglePlay());

    const speedBtn = this.container.querySelector('#btn-ephem-speed');
    if (speedBtn) {
      speedBtn.addEventListener('click', () => {
        const speeds = [0.5, 1.0, 2.0, 4.0];
        const nextIdx = (speeds.indexOf(this.playbackSpeed) + 1) % speeds.length;
        this.playbackSpeed = speeds[nextIdx];
        speedBtn.textContent = `${this.playbackSpeed.toFixed(1)}x`;
        if (this.isPlaying) {
          this.pausePlay();
          this.startPlay();
        }
      });
    }

    const loopBtn = this.container.querySelector('#btn-ephem-loop');
    if (loopBtn) {
      loopBtn.addEventListener('click', () => {
        this.isLooping = !this.isLooping;
        loopBtn.classList.toggle('active', this.isLooping);
      });
    }
  }

  private setHour(h: number): void {
    this.selectedHour = Math.max(0, Math.min(23, h));
    this.updateHourUI();
    this.updateMapLayer();
  }

  private stepHour(delta: number): void {
    let nextH = this.selectedHour + delta;
    if (nextH > 23) {
      nextH = this.isLooping ? 0 : 23;
    } else if (nextH < 0) {
      nextH = this.isLooping ? 23 : 0;
    }
    this.setHour(nextH);
  }

  private togglePlay(): void {
    if (this.isPlaying) this.pausePlay();
    else this.startPlay();
  }

  private startPlay(): void {
    this.isPlaying = true;
    const playBtn = this.container?.querySelector('#btn-ephem-play');
    if (playBtn) {
      playBtn.classList.add('playing');
      playBtn.textContent = '⏸';
    }
    const intervalMs = Math.round(1000 / this.playbackSpeed);
    this.playIntervalId = setInterval(() => {
      let nextH = this.selectedHour + 1;
      if (nextH > 23) {
        if (!this.isLooping) {
          this.pausePlay();
          return;
        }
        nextH = 0;
      }
      this.setHour(nextH);
    }, intervalMs);
  }

  private pausePlay(): void {
    this.isPlaying = false;
    if (this.playIntervalId) {
      clearInterval(this.playIntervalId);
      this.playIntervalId = null;
    }
    const playBtn = this.container?.querySelector('#btn-ephem-play');
    if (playBtn) {
      playBtn.classList.remove('playing');
      playBtn.textContent = '▶';
    }
  }

  private updateHourUI(): void {
    if (!this.container) return;

    const slider = this.container.querySelector('#ephem-timeline-slider') as HTMLInputElement;
    if (slider) slider.value = String(this.selectedHour);

    const badge = this.container.querySelector('#ephem-slider-hour-badge');
    if (badge) badge.textContent = `${String(this.selectedHour).padStart(2, '0')}:00 Local`;

    const selDateStr = `${this.selectedDate.getFullYear()}-${String(this.selectedDate.getMonth() + 1).padStart(2, '0')}-${String(this.selectedDate.getDate()).padStart(2, '0')}`;
    const m = this.metricsCache.get(selDateStr);
    if (m) {
      const curScore = (m.hourlyScores && m.hourlyScores[this.selectedHour] !== undefined) ? m.hourlyScores[this.selectedHour] : 0;
      const scoreBadge = this.container.querySelector('#ephem-current-hour-score');
      if (scoreBadge) scoreBadge.textContent = `At ${String(this.selectedHour).padStart(2, '0')}:00: ${curScore.toFixed(1)} / 10`;
      this.drawDiurnalSparkline(m.hourlyScores);
    }

    this.drawMonthlyDiurnalCurve();

    // Celestial altitudes
    const state = store.getState();
    const lat = state.selectedPoi ? state.selectedPoi.lat : state.mapCenter[1];
    const lon = state.selectedPoi ? state.selectedPoi.lng : state.mapCenter[0];
    const activeAstro = this.getCelestial(new Date(this.selectedDate.getFullYear(), this.selectedDate.getMonth(), this.selectedDate.getDate(), this.selectedHour, 0, 0));
    const curSunAlt = this.altitude(lat, lon, activeAstro.gmst, activeAstro.sun.ra, activeAstro.sun.dec);
    const curMoonAlt = this.moonAltitude(lat, lon, activeAstro.gmst, activeAstro.moon.ra, activeAstro.moon.dec);

    let celestialStatus = '☀️ Daylight / Twilight';
    if (curSunAlt < -18) {
      if (curMoonAlt <= 0 || activeAstro.moon.fraction <= 0.03) celestialStatus = '🌌 Pristine Astronomical Dark';
      else celestialStatus = `🌙 Moonlit Night (${Math.round(activeAstro.moon.fraction * 100)}% illum)`;
    }

    const readout = this.container.querySelector('.ephem-dock-celestial-readout');
    if (readout) {
      readout.innerHTML = `
        <span>☀️ Sun: <strong style="color: ${curSunAlt < -18 ? '#38bdf8' : '#f59e0b'};">${curSunAlt.toFixed(1)}°</strong></span>
        <span>🌙 Moon: <strong style="color: #e2e8f0;">${curMoonAlt.toFixed(1)}°</strong></span>
        <span class="ephem-celestial-badge" style="background: rgba(56, 189, 248, 0.15); color: #38bdf8; border: 1px solid rgba(56, 189, 248, 0.3); padding: 1px 6px; border-radius: 4px; font-weight: 700;">
          ${celestialStatus}
        </span>
      `;
    }
  }
}

export default SolarLunarDockPanel;
